import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

export default function BatchDetailHeader({ batch, archived, memberCount }) {
  const created = formatDate(batch?.created_at);
  const updated = formatDate(batch?.updated_at);

  return (
    <Box
      sx={{
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        mb: 3,
      }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
        sx={{ mb: 1.5, '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}
      >
        <Link
          component={RouterLink}
          to="/teacher/batches"
          underline="hover"
          color="text.secondary"
          variant="body2"
          sx={{ fontWeight: 500 }}
        >
          Manage Classes
        </Link>
        <Typography color="text.primary" variant="body2" sx={{ fontWeight: 600 }}>
          {batch?.name || 'Batch'}
        </Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: -0.02, lineHeight: 1.2 }}>
              {batch?.name}
            </Typography>
            <Chip
              size="small"
              label={archived ? 'Archived' : 'Active'}
              color={archived ? 'default' : 'success'}
              variant={archived ? 'outlined' : 'filled'}
              sx={{
                fontWeight: 600,
                height: 28,
                ...(archived
                  ? {}
                  : {
                      bgcolor: 'success.main',
                      color: 'success.contrastText',
                      '& .MuiChip-label': { px: 1.25 },
                    }),
              }}
            />
          </Stack>
          {batch?.description ? (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, maxWidth: 720, lineHeight: 1.6 }}>
              {batch.description}
            </Typography>
          ) : null}
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ mt: 1.75 }} alignItems="center">
            <Typography variant="body2" color="text.secondary" component="span">
              <strong style={{ color: 'inherit', fontWeight: 600 }}>{memberCount}</strong>
              {' '}
              {memberCount === 1 ? 'student' : 'students'}
            </Typography>
            {created ? (
              <Stack direction="row" alignItems="center" spacing={0.5} color="text.secondary">
                <CalendarTodayOutlinedIcon sx={{ fontSize: 16, opacity: 0.75 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Created {created}
                  {updated && updated !== created ? ` · Updated ${updated}` : ''}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
